import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Collapse,
  Divider,
  Grid,
  TextField,
} from '@mui/material';
import { Box } from '@mui/system';
import clsx from 'clsx';
import { FormikValues, useFormik } from 'formik';
import { forEach, get, isObject, isString } from 'lodash';
import { ComponentType, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ROUTES } from '../../../constants/routes';
import { useRouter } from '../../../hooks';
import { ApiAppeal, ApiOneId } from '../../../http';
import { DirectionsSelect } from '../../directions';
import { DistrictsSelect } from '../../districts';
import { Page } from '../../page';
import { RegionsSelect } from '../../regions';
import {
  AppealEditValidationForm,
  FIELD_APPLICATION,
  FIELD_DESCRIPTION,
  FIELD_DIRECTION,
  FIELD_DISTRICT,
  FIELD_FIRST_NAME,
  FIELD_LAST_NAME,
  FIELD_MIDDLE_NAME,
  FIELD_PIN,
  FIELD_REGION,
} from './applications-edit-schema';

interface AppealsEditFormProps {
  className?: string;
}

export const AppealsEditForm: ComponentType<AppealsEditFormProps> = ({ className }) => {
  const router = useRouter();
  const { t } = useTranslation();
  const [error, setError] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleUserByPin = () => {
    setIsLoading(true);
    const pin = get(formik, ['values', FIELD_PIN], null);
    ApiOneId.fetchUserByPin(pin)
      .then(({ firstName, lastName, middleName }) => {
        formik.setFieldValue([FIELD_FIRST_NAME], firstName);
        formik.setFieldValue([FIELD_LAST_NAME], lastName);
        formik.setFieldValue([FIELD_MIDDLE_NAME], middleName);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSubmit = (values: FormikValues) => {
    setIsLoading(true);
    ApiAppeal.createOne(values)
      .then(() => {
        router.push(ROUTES.APPEALS.ROOT);
      })
      .catch(({ message }) => {
        setError(message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleFieldChange = (field: string, e: any) => {
    formik.setFieldValue(field, e.target.value);
    formik.setFieldError(field, null);
  };

  const hasError = (field: string) => {
    return (
      get(formik, ['touched', ...field.split('.')], false) &&
      Boolean(get(formik, ['errors', ...field.split('.')], false))
    );
  };

  const getErrorMessage = (field: string) => {
    if (!hasError(field)) return ' ';
    const key = get(formik, ['errors', ...field.split('.')], 'error');
    if (key.includes('error.')) {
      return t(key, 'Ошибка');
    }
    return key;
  };

  const formik: any = useFormik({
    initialValues: {
      [FIELD_APPLICATION]: '',
      [FIELD_PIN]: '',
      [FIELD_FIRST_NAME]: '',
      [FIELD_LAST_NAME]: '',
      [FIELD_MIDDLE_NAME]: '',
      [FIELD_DIRECTION]: '',
      [FIELD_REGION]: '',
      [FIELD_DISTRICT]: '',
      [FIELD_DESCRIPTION]: '',
    },
    validationSchema: AppealEditValidationForm,
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: handleSubmit,
  });

  const inputProps = (field: string) => ({
    id: `create-${field}`,
    error: hasError(field),
    helperText: getErrorMessage(field),
    disabled: isLoading,
    fullWidth: true,
    size: 'small',
    InputProps: {
      autoComplete: 'off',
      disabled: isLoading,
    },
  });

  useEffect(() => {
    if (isObject(error)) {
      forEach(error, (value, field) => formik.setFieldError(field, value[0]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return (
    <Page title={t('appeals.create', 'Создать апелляцию')}>
      <Card>
        <CardContent>
          <form onSubmit={formik.handleSubmit} className={clsx(className)}>
            <Grid container direction="column" spacing={1} mb={1}>
              <Grid item>
                <TextField
                  {...inputProps(FIELD_APPLICATION)}
                  {...formik.getFieldProps(FIELD_APPLICATION)}
                  onChange={(e: any) => handleFieldChange(FIELD_APPLICATION, e)}
                  label={t('application.id', 'ID заявки')}
                />
              </Grid>
              <Grid item container columnSpacing={3}>
                <Grid item xs={8}>
                  <TextField
                    {...inputProps(FIELD_PIN)}
                    {...formik.getFieldProps(FIELD_PIN)}
                    onChange={(e: any) => handleFieldChange(FIELD_PIN, e)}
                    label={t('profile.pin', 'ПИНФЛ')}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Box style={{ height: 21 }} />
                  <Button
                    onClick={handleUserByPin}
                    variant="contained"
                    color="primary"
                    type="button"
                    disabled={isLoading}
                    fullWidth
                  >
                    {t('action.search.by.pin', 'Найти по ПИНФЛ')}
                  </Button>
                </Grid>
              </Grid>
              <Grid item>
                <TextField
                  {...inputProps(FIELD_LAST_NAME)}
                  {...formik.getFieldProps(FIELD_LAST_NAME)}
                  onChange={(e: any) => handleFieldChange(FIELD_LAST_NAME, e)}
                  label={t('profile.lastName', 'Фамилия')}
                />
              </Grid>
              <Grid item>
                <TextField
                  {...inputProps(FIELD_FIRST_NAME)}
                  {...formik.getFieldProps(FIELD_FIRST_NAME)}
                  onChange={(e: any) => handleFieldChange(FIELD_FIRST_NAME, e)}
                  label={t('profile.firstName', 'Имя')}
                />
              </Grid>
              <Grid item>
                <TextField
                  {...inputProps(FIELD_MIDDLE_NAME)}
                  {...formik.getFieldProps(FIELD_MIDDLE_NAME)}
                  onChange={(e: any) => handleFieldChange(FIELD_MIDDLE_NAME, e)}
                  label={t('profile.middleName', 'Отчество')}
                />
              </Grid>
              <Grid item>
                <DirectionsSelect
                  {...inputProps(FIELD_DIRECTION)}
                  {...formik.getFieldProps(FIELD_DIRECTION)}
                  onChange={(e: any) => handleFieldChange(FIELD_DIRECTION, e)}
                  label={t('direction', 'Направление')}
                />
              </Grid>
              <Grid item>
                <RegionsSelect
                  {...inputProps(FIELD_REGION)}
                  {...formik.getFieldProps(FIELD_REGION)}
                  onChange={(e: any) => handleFieldChange(FIELD_REGION, e)}
                  label={t('Регион')}
                />
              </Grid>
              <Grid item>
                <DistrictsSelect
                  {...inputProps(FIELD_DISTRICT)}
                  {...formik.getFieldProps(FIELD_DISTRICT)}
                  onChange={(e: any) => handleFieldChange(FIELD_DISTRICT, e)}
                  region={get(formik, ['values', FIELD_REGION], null)}
                  label={t('district', 'Район')}
                />
              </Grid>
              <Grid item>
                <TextField
                  {...inputProps(FIELD_DESCRIPTION)}
                  {...formik.getFieldProps(FIELD_DESCRIPTION)}
                  onChange={(e: any) => handleFieldChange(FIELD_DESCRIPTION, e)}
                  label={t('description', 'Описание')}
                  multiline
                  rows={4}
                />
              </Grid>
            </Grid>
            {isString(error) && (
              <Collapse in={!!error}>
                <Alert severity="error">{error}</Alert>
              </Collapse>
            )}
            <Divider sx={{ mt: 1, mb: 3 }} />
            <LoadingButton
              type="submit"
              variant="contained"
              color="primary"
              loading={isLoading}
              fullWidth
            >
              {t('action.create', 'Создать')}
            </LoadingButton>
          </form>
        </CardContent>
      </Card>
    </Page>
  );
};
